import { isAxiosError } from 'axios';
import { useState } from 'react';
import type { ComponentProps } from 'react';

import type { FileAttachmentContentBlockBodyT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { cn } from '@/lib/utils';
import { Dialog, DialogContent } from '@/shared/ui/shadcn/dialog';

import { useAttachmentViewUrlQuery } from '../model/useAttachmentViewUrlQuery.js';

export interface FileAttachmentContentBlockProps extends Omit<ComponentProps<'div'>, 'children'> {
  userId: string;
  body: FileAttachmentContentBlockBodyT;
}

export function FileAttachmentContentBlock({
  userId,
  body,
  className,
  ...props
}: FileAttachmentContentBlockProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const filename =
    typeof body.filename === 'string' && body.filename.trim() !== '' ? body.filename : null;
  const attachmentId = body.attachmentId;
  const fileType = body.fileType;

  const query = useAttachmentViewUrlQuery({
    userId,
    attachmentId,
    // We require fileType to be known; if it's null we disable the query and show a fallback.
    fileType: fileType ?? 'image',
    enabled: fileType != null,
  });

  if (fileType == null) {
    return (
      <div className={cn('text-xs text-muted-foreground', className)} {...props}>
        Unknown attachment type
      </div>
    );
  }

  if (query.status === 'pending') {
    return (
      <div className={cn('text-xs text-muted-foreground', className)} {...props}>
        Loading attachment…
      </div>
    );
  }

  if (query.status === 'error') {
    const status = isAxiosError(query.error) ? query.error.response?.status : undefined;
    const message =
      status === 403 || status === 404 ? 'Attachment not accessible' : 'Failed to load attachment';

    return (
      <div className={cn('text-xs text-muted-foreground', className)} {...props}>
        {message}
      </div>
    );
  }

  if (!query.data) {
    return null;
  }

  const { viewUrl } = query.data;
  const label = filename ?? (fileType === 'image' ? 'Uploaded image' : 'View attachment');

  // viewUrl may be null if the requested variant is not available for this content type
  if (!viewUrl) {
    return (
      <div className={cn('text-xs text-muted-foreground', className)} {...props}>
        Attachment not available
      </div>
    );
  }

  if (fileType === 'image') {
    return (
      <div className={cn('max-w-sm', className)} {...props}>
        <button
          type="button"
          onClick={() => setIsLightboxOpen(true)}
          className="inline-block cursor-zoom-in rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-haspopup="dialog"
        >
          <img
            src={viewUrl}
            alt={label}
            loading="lazy"
            decoding="async"
            className="max-h-80 w-full rounded-md border object-contain"
          />
          <span className="sr-only">Enlarge image</span>
        </button>

        <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
          <DialogContent
            showCloseButton
            overlayClassName="bg-background/80 backdrop-blur-sm"
            className="flex h-[90vh] w-[90vw] max-w-none items-center justify-center border-none bg-transparent p-0 shadow-none"
            aria-label={label}
          >
            <img
              src={viewUrl}
              alt={label}
              className="max-h-full max-w-full rounded-lg object-contain"
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className={cn('text-xs', className)} {...props}>
      <a
        href={viewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary underline underline-offset-2"
      >
        {label}
      </a>
      <span className="sr-only">Open attachment in new tab</span>
    </div>
  );
}
